import { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import { object, string } from "yup";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";

import ReactSelectWithFormik from "components/lib/Shared/DropdownSelect";
import Input from "components/lib/Shared/Input";
import Button from "components/lib/Shared/Button";
import {
  addSubscriptionAsync,
  editSubscriptionAsync,
  getSubscriptionsAsync,
} from "state/slices/subscription";
import { Constants } from "config/constants";
import { useToast } from "hooks/useToast";

const validationSchema = object().shape({
  product: string().required("Product is required"),
  name: string().required("Name is required"),
  description: string()
    .trim("Description cannot include leading and trailing spaces")
    .strict(true)
    .required("Description is required"),
});

const SubscriptionOverview = () => {
  const cookieRole = Cookies.get("role");
  const { toast } = useToast();
  const dispatch = useDispatch();
  const { products } = useSelector((state) => state.productSlice);
  const { selectedSubscription } = useSelector(
    (state) => state.subscriptionSlice
  );
  const { subscriptions } = useSelector((state) => state.subscriptionSlice);
  const { organization, organizationName } = useSelector(
    (state) => state.activeDashboardSlice
  );

  const getOrderNumber = (name) => {
    switch (name) {
      case Constants.SUBSCRIPTION_PACKAGE_NAMES_OBJECT.BASIC:
      case Constants.SUBSCRIPTION_PACKAGE_NAMES_OBJECT.ESSENTIALS:
        return 1;
      case Constants.SUBSCRIPTION_PACKAGE_NAMES_OBJECT.PRINT:
      case Constants.SUBSCRIPTION_PACKAGE_NAMES_OBJECT.PLUS:
        return 2;
      case Constants.SUBSCRIPTION_PACKAGE_NAMES_OBJECT.PREMIUM:
      case Constants.SUBSCRIPTION_PACKAGE_NAMES_OBJECT.PRO:
        return 3;
      default:
        return 4;
    }
  };

  const handleSubmit = async (data) => {
    let response = {};
    if (selectedSubscription?._id) {
      response = await dispatch(
        editSubscriptionAsync({
          subscriptionId: selectedSubscription?._id,
          data,
        })
      );
    } else {
      data = {
        ...data,
        orderNumber: getOrderNumber(data.name),
      };
      // Link orgAdmin to the subscription
      if (
        cookieRole === Constants.COOKIE_ROLES.ORG_ADMIN &&
        organization.isOrgAdmin
      ) {
        data = {
          ...data,
          orgAdmin: organization._id,
        };
      }
      response = await dispatch(addSubscriptionAsync({ data }));
    }
    if (response.error) {
      toast(
        "error",
        response.payload?.messageText ?? "Something went wrong please try again"
      );
    } else toast("success", "Successfully saved the details");
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        service: "",
        product: "",
        name: "",
        description: "",
        subscribersLimit: 1,
        ...selectedSubscription,
        product: selectedSubscription?.product?._id ?? "",
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, isSubmitting }) => {
        const [subscriptionNameOptions, setSubscriptionNameOptions] = useState(
          []
        );

        useEffect(() => {
          const subscriptionsNameAlreadyTaken = new Set(
            subscriptions.items.map((subscription) => subscription.name)
          );
          subscriptionsNameAlreadyTaken.delete(selectedSubscription.name);
          let subscriptionNameOptions = [];
          if (cookieRole === Constants.COOKIE_ROLES.SUPER_ADMIN) {
            subscriptionNameOptions =
              Constants.SUBSCRIPTION_PACKAGE_NAMES.SUPER_ADMIN.filter(
                (subscriptionName) =>
                  !subscriptionsNameAlreadyTaken.has(subscriptionName.value)
              );
          } else {
            subscriptionNameOptions =
              Constants.SUBSCRIPTION_PACKAGE_NAMES.ORG_ADMIN.filter(
                (subscriptionName) =>
                  !subscriptionsNameAlreadyTaken.has(subscriptionName.value)
              );
          }

          setSubscriptionNameOptions(subscriptionNameOptions);
        }, [values?.name, selectedSubscription?.name, cookieRole]);

        return (
          <Form className="text-sm w-full">
            <div className="shadow sm:rounded-md sm:overflow-hidden">
              <div className="bg-white py-6 px-4 space-y-6 sm:p-6">
                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6 sm:col-span-3">
                    <Field
                      name="name"
                      label="SFL Subscription Name"
                      options={subscriptionNameOptions}
                      component={ReactSelectWithFormik}
                      className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      id="name"
                      placeholder="Select Subscription Name"
                      value={values?.name ?? ""}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <Field
                      name="product"
                      label="SFL Product"
                      options={products.items.map((product) => ({
                        label: product.name,
                        value: product._id,
                      }))}
                      component={ReactSelectWithFormik}
                      className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      id="product"
                      placeholder="Select Product"
                      value={values?.product ?? ""}
                    />
                  </div>
                </div>
                <div>
                  <div className="mt-2">
                    <Field
                      component={Input}
                      label="Product Summary"
                      id="description"
                      name="description"
                      rows="5"
                      renderTextarea
                      placeholder="Write a brief description of the product"
                      className="block w-full rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:py-1.5 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>
              </div>
            </div>
            {
              <div className="mt-6 flex items-center justify-end gap-x-4">
                <Button
                  type="submit"
                  loadingText="Saving..."
                  isLoading={isSubmitting}
                >
                  Save
                </Button>
              </div>
            }
          </Form>
        );
      }}
    </Formik>
  );
};

export default SubscriptionOverview;
